import Box from "@mui/material/Box";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  Colors,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import React, { useState, useRef, useCallback, useEffect } from "react";
import "./App.css";
import Footer from "./components/Footer";
import Header from "./components/Header";
import ModeSwitcher from "./components/mode-switcher/ModeSwitcher";
import SimulationGrid from "./components/SimulationGrid";
import { GlobalSettingsProvider } from "./context/GlobalSettingsContext";
import { useSimulationFormModel } from "./hooks/useSimulationFormModel";
import { useStatePersistence } from "./hooks/useStatePersistence";
import { ModeProvider, useMode } from "./mode/ModeContext";
import { modes } from "./mode/modes";
import {
  SavingsAccountParams,
  SimulationFormInputs,
  SimulationFormModel,
} from "./types/forms";

ChartJS.register(
  CategoryScale,
  Colors,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export const defaultParams: SimulationFormInputs = {
  principal: undefined,
  interestOnly: false,
  termYears: undefined,
  interestRate: undefined,
  desiredMonthlyPayment: undefined,
  additionalCapitalRepayments: false,
  dealLength: undefined,
  maxAffordable: undefined,
  savingsInterestRate: 0.0,
  saveAtStart: false,
  additionalCapitalRepaymentPercentageLimit: 10,
  reduceTermOnAdditionalCapitalRepayment: false,
  savingsAccounts: [] as SavingsAccountParams[],
};

export const ExpandedSimulationContext = React.createContext<{
  expanded: number | null;
  setExpanded: React.Dispatch<React.SetStateAction<number | null>>;
}>({
  expanded: null,
  setExpanded: () => {},
});

function AppContent({
  simulationFormModelRef,
}: {
  simulationFormModelRef: React.MutableRefObject<SimulationFormModel | null>;
}) {
  const { mode, setMode } = useMode();
  const Form = mode ? modes[mode].components.Form : null;
  const canEdit = mode ? (modes[mode].canEdit ?? true) : false;
  const canDuplicate = mode ? (modes[mode].canDuplicate ?? true) : false;
  const canDelete = mode ? (modes[mode].canDelete ?? true) : false;

  const [editingIndex, setEditingIndex] = useState<number | undefined>(
    undefined,
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const simulationFormModel = useSimulationFormModel();

  // Show form only if there are no results (e.g., first time in mode)
  const [showForm, setShowForm] = useState<boolean>(
    simulationFormModel.results.length === 0,
  );

  // Set expanded based on mode: 0 for singleSimulation, null otherwise
  const [expanded, setExpanded] = useState<number | null>(
    mode === "singleSimulation" && simulationFormModel.results.length > 0
      ? 0
      : null,
  );

  const prevModeRef = useRef<typeof mode>(mode);
  const [shouldPersist, setShouldPersist] = useState(false);

  // Expose simulationFormModel to parent via ref
  simulationFormModelRef.current = simulationFormModel;

  // Get persist function to auto-save state to session history
  const persistState = useStatePersistence();

  // Persist when flag is set and simulations have been updated
  useEffect(() => {
    if (shouldPersist) {
      persistState(simulationFormModel.simulations);
      setShouldPersist(false);
    }
  }, [shouldPersist, simulationFormModel.simulations, persistState]);

  useEffect(() => {
    if (prevModeRef.current !== mode) {
      // When entering a mode (from null to non-null), show the form
      if (prevModeRef.current === null && mode !== null) {
        setShowForm(true);
        setIsEditing(false);
        setEditingIndex(undefined);
      }
      // When exiting to mode selection (from non-null to null)
      if (prevModeRef.current !== null && mode === null) {
        setExpanded(null);
        setShowForm(true); // Reset to show form when entering a new mode
        setIsEditing(false);
        setEditingIndex(undefined);
      }
    }
    prevModeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [showForm, expanded, mode]);

  const handleAddOrUpdate = async () => {
    await simulationFormModel.runUpdatedSimulations();
    setShouldPersist(true); // Trigger persistence after state updates
    if (simulationFormModel.simulations.length > 1) {
      setExpanded(null);
    } else {
      setExpanded(simulationFormModel.results.length - 1);
    }
    setShowForm(false);
  };

  const handleAdd = () => {
    setIsEditing(false);
    setShowForm(true);
  };

  const handleEdit = (idx?: number) => {
    setIsEditing(true);
    setEditingIndex(idx);
    setShowForm(true);
  };

  const handleDuplicate = (idx: number): number => {
    simulationFormModel.duplicateSimulation(idx);
    setShouldPersist(true); // Trigger persistence after state updates
    return simulationFormModel.results.length - 1;
  };

  const handleDelete = (idx: number) => {
    simulationFormModel.removeSimulation(idx);
    setShouldPersist(true); // Trigger persistence after state updates
    if (editingIndex === idx) setEditingIndex(undefined);
  };

  const handleCancel = () => {
    // Check if in edit mode and there are unsaved changes
    const hasUnsavedChanges = simulationFormModel.simulations.some(
      (sim) => sim.updated,
    );

    if (isEditing && hasUnsavedChanges) {
      setShowCancelConfirm(true);
      return;
    }

    performCancel();
  };

  const performCancel = () => {
    if (simulationFormModel.results.length === 0) {
      setMode(null);
    } else {
      setShowForm(false);
    }
    setEditingIndex(undefined);
    setShowCancelConfirm(false);
  };

  const handleConfirmCancel = () => {
    // Revert simulation inputs back to their last-run state
    simulationFormModel.revertChanges();
    performCancel();
  };

  const handleDismissCancelConfirm = () => {
    setShowCancelConfirm(false);
  };

  return (
    <ExpandedSimulationContext.Provider value={{ expanded, setExpanded }}>
      <Box
        className="App"
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
        }}
      >
        <Header simulationFormModel={simulationFormModel} formOpen={showForm} />

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            py: 2,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 1200 }}>
            {showForm && Form ? (
              <>
                <ModeSwitcher showCancel={true} toggleCancel={handleCancel} />
                <Form
                  model={simulationFormModel}
                  onSubmit={handleAddOrUpdate}
                  onCancel={handleCancel}
                  isEditing={isEditing}
                  editingIndex={editingIndex}
                />
              </>
            ) : (
              <>
                {expanded === null && (
                  <ModeSwitcher
                    showAdd={!showForm && (mode || false) && mode === "manual"}
                    showEdit={
                      !showForm &&
                      (mode || false) &&
                      mode !== "manual" &&
                      simulationFormModel.results.length > 0
                    }
                    toggleAdd={handleAdd}
                    toggleEdit={
                      mode === "singleSimulation"
                        ? () => handleEdit(0)
                        : handleEdit
                    }
                  />
                )}
                <SimulationGrid
                  simulations={simulationFormModel.results}
                  simulationFormModel={simulationFormModel}
                  handleEdit={canEdit ? handleEdit : undefined}
                  handleDuplicate={canDuplicate ? handleDuplicate : undefined}
                  handleDelete={canDelete ? handleDelete : undefined}
                />
              </>
            )}
          </Box>
        </Box>

        <Footer />

        <Dialog
          open={showCancelConfirm}
          onClose={handleDismissCancelConfirm}
          aria-labelledby="cancel-confirm-title"
          aria-describedby="cancel-confirm-description"
        >
          <DialogTitle id="cancel-confirm-title">Discard changes?</DialogTitle>
          <DialogContent>
            <DialogContentText id="cancel-confirm-description">
              You have unsaved changes. Are you sure you want to cancel and
              discard them?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDismissCancelConfirm}>Keep Editing</Button>
            <Button onClick={handleConfirmCancel} color="error" autoFocus>
              Discard Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ExpandedSimulationContext.Provider>
  );
}

function AppInner({
  simulationFormModelRef,
}: {
  simulationFormModelRef: React.MutableRefObject<SimulationFormModel | null>;
}) {
  const { resumeKey } = useMode();

  return (
    <AppContent
      key={resumeKey}
      simulationFormModelRef={simulationFormModelRef}
    />
  );
}

export default function App() {
  const simulationFormModelRef = useRef<SimulationFormModel | null>(null);

  const handleClearSimulations = useCallback(() => {
    const model = simulationFormModelRef.current;
    if (model) {
      // Clear all simulations when returning to mode selection
      // Remove from end to start to avoid index shifting issues
      for (let i = model.simulations.length - 1; i >= 0; i--) {
        model.removeSimulation(i);
      }
    }
  }, []);

  return (
    <GlobalSettingsProvider>
      <ModeProvider onClearSimulations={handleClearSimulations}>
        <AppInner simulationFormModelRef={simulationFormModelRef} />
      </ModeProvider>
    </GlobalSettingsProvider>
  );
}
